import React, { useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Sparkles, Link2, Code2, Download, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import api from '../../api';

/**
 * Narrative-proposal editor for ProposalDetail.
 *
 * MVP editor: full JSON view + dedicated fields for the most-edited blocks
 * (cover headline/sub, understanding items, options). For deeper edits users
 * can either edit the JSON directly or generate the JSON with their LLM
 * (ChatGPT/Claude) and paste it into "Paste JSON".
 *
 * All visual formatting/headings/layout are owned by the public viewer
 * (PublicProposalNarrative). This section ONLY edits the content.
 */
export default function NarrativeSection({ proposal, onUpdated }) {
    const proposalId = proposal._id || proposal.id;
    const [open, setOpen] = useState(Boolean(proposal.narrative));
    const [busy, setBusy] = useState(false);
    const [jsonOpen, setJsonOpen] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const narrative = proposal.narrative;
    const hasNarrative = !!narrative && Object.keys(narrative).length > 0;
    const publicUrl = `${window.location.origin}/p/${proposal.public_token}/narrative`;

    const seed = async () => {
        if (hasNarrative && !window.confirm('Replace the current narrative with the default template? Existing edits will be lost.')) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/proposals/${proposalId}/narrative/seed`);
            onUpdated({ ...proposal, narrative: data.narrative });
            toast.success('Default narrative template loaded');
        } catch { toast.error('Could not load default template'); }
        finally { setBusy(false); }
    };

    const saveNarrative = async (next) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/proposals/${proposalId}/narrative`, { narrative: next });
            onUpdated(data);
            toast.success('Narrative saved');
        } catch { toast.error('Could not save narrative'); }
        finally { setBusy(false); }
    };

    const clearNarrative = async () => {
        if (!window.confirm('Remove the narrative version entirely? The standard proposal stays intact.')) return;
        await saveNarrative({});
    };

    const importJson = async () => {
        const trimmed = (jsonText || '').trim();
        if (!trimmed) { toast.error('Please paste some content first.'); return; }
        // Auto-detect: if it starts with < (HTML/DOCTYPE/tag), save as html mode.
        if (trimmed.startsWith('<')) {
            await saveNarrative({ html: trimmed });
            setJsonOpen(false);
            setJsonText('');
            toast.success('HTML narrative saved');
            return;
        }
        let parsed;
        try { parsed = JSON.parse(trimmed); }
        catch (e) {
            toast.error('Could not parse. Paste either JSON (starts with {) or HTML (starts with <).');
            return;
        }
        if (!parsed || typeof parsed !== 'object') { toast.error('JSON must be an object'); return; }
        await saveNarrative(parsed);
        setJsonOpen(false);
        setJsonText('');
    };

    const exportJson = async () => {
        try {
            const { data } = hasNarrative
                ? { data: narrative }
                : await api.get(`/proposals/narrative/template-default`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${proposal.proposal_number || 'proposal'}-narrative.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Export failed'); }
    };

    const copyPublicLink = () => {
        navigator.clipboard.writeText(publicUrl).then(() => toast.success('Narrative link copied'));
    };

    // ── Convenience editors for the most-edited blocks ──
    const updateBlock = (path, value) => {
        const next = JSON.parse(JSON.stringify(narrative || {}));
        const keys = path.split('.');
        let cur = next;
        for (let i = 0; i < keys.length - 1; i++) {
            if (cur[keys[i]] == null) cur[keys[i]] = {};
            cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = value;
        saveNarrative(next);
    };

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)]" data-testid="narrative-section">
            <button
                type="button"
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
                data-testid="narrative-toggle"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Sparkles size={16} style={{ color: 'var(--bm-primary)' }} />
                    <div className="min-w-0">
                        <div className="font-display text-[15px] font-semibold">Narrative Proposal <span className="text-[11px] font-medium text-[color:var(--bm-text-muted)] ml-1 uppercase tracking-wider">{hasNarrative ? 'Active' : 'Inactive'}</span></div>
                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-0.5">
                            {hasNarrative
                                ? `Branded narrative view at /p/${proposal.public_token}/narrative — sits alongside the standard proposal.`
                                : 'Add a polished, branded narrative version of this proposal — editable wording, options and timelines.'}
                        </div>
                    </div>
                </div>
                <ChevronDown size={16} className={`text-[color:var(--bm-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="px-6 pb-6 pt-2 border-t border-[color:var(--bm-border)] space-y-5">
                    {/* Top action row */}
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" size="sm" onClick={seed} disabled={busy} data-testid="narrative-seed-btn">
                            <Sparkles size={14} className="mr-1.5" /> Load default template
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setJsonText(hasNarrative ? (typeof narrative === 'object' && narrative.html ? narrative.html : JSON.stringify(narrative, null, 2)) : ''); setJsonOpen(true); }} data-testid="narrative-paste-btn">
                            <Code2 size={14} className="mr-1.5" /> {hasNarrative ? 'Edit / Paste narrative' : 'Paste narrative (JSON or HTML)'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={exportJson} data-testid="narrative-export-btn">
                            <Download size={14} className="mr-1.5" /> {hasNarrative ? 'Download current' : 'Download template'}
                        </Button>
                        {hasNarrative && (
                            <>
                                <Button variant="outline" size="sm" onClick={copyPublicLink} data-testid="narrative-copy-link-btn">
                                    <Link2 size={14} className="mr-1.5" /> Copy public link
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, '_blank')} data-testid="narrative-preview-btn">
                                    <ExternalLink size={14} className="mr-1.5" /> Preview
                                </Button>
                                <Button variant="outline" size="sm" onClick={clearNarrative} className="ml-auto text-rose-600 hover:text-rose-700" data-testid="narrative-clear-btn">
                                    <Trash2 size={14} className="mr-1.5" /> Remove
                                </Button>
                            </>
                        )}
                    </div>

                    {!hasNarrative && (
                        <div className="rounded-lg border border-dashed border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] px-5 py-6 text-center">
                            <p className="text-[13.5px] text-[color:var(--bm-text)] font-medium mb-1">No narrative yet.</p>
                            <p className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                                Click <strong>Load default template</strong> to start from our standard structure, or <strong>Paste narrative</strong> to drop in JSON or a full HTML document from your AI assistant.
                            </p>
                        </div>
                    )}

                    {hasNarrative && narrative.html && (
                        <div className="rounded-lg border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] px-5 py-4">
                            <p className="text-[13px] font-medium text-[color:var(--bm-text)]">HTML narrative active</p>
                            <p className="text-[12px] text-[color:var(--bm-text-muted)] mt-1">
                                The pasted HTML is rendered on the public page. The interactive option picker + signature flow are appended automatically below your content. Click <strong>Edit / Paste narrative</strong> to revise or replace it.
                            </p>
                        </div>
                    )}

                    {hasNarrative && !narrative.html && (
                        <div className="space-y-5">
                            {/* Cover block */}
                            <BlockEditor label="Cover / hero">
                                <Field label="Eyebrow"     value={narrative.cover?.eyebrow || ''}     onSave={v => updateBlock('cover.eyebrow', v)} />
                                <Field label="Headline (HTML allowed: <em>emphasis</em>)" value={narrative.cover?.headline || ''} onSave={v => updateBlock('cover.headline', v)} />
                                <Field label="Sub-headline" textarea value={narrative.cover?.sub_headline || ''} onSave={v => updateBlock('cover.sub_headline', v)} />
                            </BlockEditor>

                            {/* Understanding */}
                            <BlockEditor label='Section 1 — "What we heard"'>
                                <Field label="Section lead" textarea value={narrative.understanding?.lead || ''} onSave={v => updateBlock('understanding.lead', v)} />
                                <Field label="Pull-quote" value={narrative.understanding?.pull_quote || ''} onSave={v => updateBlock('understanding.pull_quote', v)} />
                                <p className="text-[11.5px] text-[color:var(--bm-text-muted)]">
                                    {narrative.understanding?.items?.length || 0} pain-point items — edit via JSON for now.
                                </p>
                            </BlockEditor>

                            {/* Options summary */}
                            <BlockEditor label="Routes forward (options)">
                                <Field label="Section lead" textarea value={narrative.options_section?.lead || ''} onSave={v => updateBlock('options_section.lead', v)} />
                                <Field label="Footnote (HTML allowed)" textarea value={narrative.options_section?.note || ''} onSave={v => updateBlock('options_section.note', v)} />
                                <p className="text-[11.5px] text-[color:var(--bm-text-muted)]">
                                    {narrative.options_section?.options?.length || 0} option(s) — edit details via JSON.
                                </p>
                            </BlockEditor>

                            <p className="text-[11.5px] text-[color:var(--bm-text-muted)] italic">
                                Tip: for modules, timelines, commercials and next steps — generate the full JSON in your AI tool (use the downloadable template as a structure prompt) then paste it back here.
                            </p>
                        </div>
                    )}

                    {/* JSON paste modal */}
                    {jsonOpen && (
                        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setJsonOpen(false)}>
                            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="narrative-json-modal">
                                <div className="px-5 py-3 border-b border-[color:var(--bm-border)] flex items-center justify-between">
                                    <h3 className="font-display text-[15px] font-semibold">Paste narrative — JSON or HTML</h3>
                                    <button onClick={() => setJsonOpen(false)} className="text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]">✕</button>
                                </div>
                                <div className="px-5 py-3 text-[12.5px] text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)] space-y-1">
                                    <p>Paste content from your AI assistant. We auto-detect the format:</p>
                                    <p>• Starts with <code className="bg-[#f5f5f0] px-1 rounded">&#123;</code> → structured JSON (per our template).</p>
                                    <p>• Starts with <code className="bg-[#f5f5f0] px-1 rounded">&lt;</code> → full HTML document (we render it as-is on the public proposal page, and the interactive options + signature flow are appended automatically).</p>
                                </div>
                                <textarea
                                    value={jsonText}
                                    onChange={e => setJsonText(e.target.value)}
                                    spellCheck={false}
                                    className="flex-1 px-5 py-4 font-mono text-[12px] resize-none focus:outline-none min-h-[300px] bg-[#fafaf7]"
                                    placeholder={'Paste either:\n  {"cover": {"headline": "..."}, ...}\nor a complete HTML document:\n  <!DOCTYPE html>\n  <html>...'}
                                    data-testid="narrative-json-textarea"
                                />
                                <div className="px-5 py-3 border-t border-[color:var(--bm-border)] flex items-center justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setJsonOpen(false)}>Cancel</Button>
                                    <Button size="sm" onClick={importJson} disabled={busy} style={{ background: 'var(--bm-primary)' }} data-testid="narrative-json-save">
                                        Save narrative
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function BlockEditor({ label, children }) {
    return (
        <div className="space-y-2.5 pb-4 border-b border-[color:var(--bm-border)] last:border-b-0 last:pb-0">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[color:var(--bm-text-subtle)]">{label}</div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function Field({ label, value, onSave, textarea }) {
    const [draft, setDraft] = useState(value);
    React.useEffect(() => setDraft(value), [value]);
    const commit = () => { if (draft !== value) onSave(draft); };
    return (
        <div>
            <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">{label}</Label>
            {textarea
                ? <Textarea value={draft || ''} onChange={e => setDraft(e.target.value)} onBlur={commit} rows={2} className="mt-1 text-[13px]" />
                : <Input value={draft || ''} onChange={e => setDraft(e.target.value)} onBlur={commit} className="mt-1 text-[13px]" />}
        </div>
    );
}
